import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LockKey, Warning } from "@phosphor-icons/react";

/**
 * Dialog reusável que exige a senha do usuário antes de executar uma ação
 * (editar ou excluir dados sensíveis). Se `open=true`, mostra o dialog;
 * ao confirmar com senha correta, chama `onConfirm()`.
 *
 * Uso:
 *   <PasswordConfirmDialog
 *     open={askPw}
 *     onOpenChange={setAskPw}
 *     action="excluir"
 *     description="Este abastecimento será removido permanentemente."
 *     onConfirm={async () => { await api.delete(...); ... }}
 *   />
 */
export function PasswordConfirmDialog({
  open,
  onOpenChange,
  action = "confirmar",
  description = "Confirme sua senha para continuar.",
  variant = "default", // "default" | "destructive"
  onConfirm,
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setPassword(""); setBusy(false); };
  const handleOpen = (v) => { if (!v) reset(); onOpenChange?.(v); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password) return toast.error("Digite sua senha");
    setBusy(true);
    try {
      await api.post("/auth/verify-password", { password });
      await onConfirm?.();
      handleOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Senha incorreta");
    } finally {
      setBusy(false);
    }
  };

  const isDestructive = variant === "destructive";
  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-sm max-h-[92vh] overflow-y-auto" data-testid="pw-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDestructive ? (
              <Warning size={22} weight="fill" className="text-red-600" />
            ) : (
              <LockKey size={22} weight="fill" className="text-blue-600" />
            )}
            Confirmar {action}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Sua senha</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              placeholder="Digite sua senha atual"
              data-testid="pw-confirm-input"
              disabled={busy}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => handleOpen(false)} disabled={busy} data-testid="pw-confirm-cancel">Cancelar</Button>
            <Button
              type="submit"
              disabled={busy || !password}
              data-testid="pw-confirm-submit"
              className={isDestructive
                ? "bg-red-600 hover:bg-red-700 hover:text-white text-white"
                : "bg-blue-600 hover:bg-blue-700 hover:text-white text-white"}
            >
              {busy ? "Verificando..." : `Confirmar ${action}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
